import React, { useState, useEffect } from 'react';
import {
    Box, Button, Select, MenuItem, FormControl, InputLabel, OutlinedInput,
    Checkbox, ListItemText, IconButton, Typography, Modal
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import apiClient from "../../api/Api.js"; // Import the pre-configured API client

const ProjectPopup = ({ open, onClose, user, onSubmit }) => {
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [projectOptions, setProjectOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    useEffect(() => {
        if (open && user?.user_id) {
            fetchProjects(user.user_id);
        }
        if (!open) {
            resetState(); // Reset state when modal closes
        }
    }, [open, user]);

    const fetchProjects = async (user_id) => {
        setIsLoading(true);
        setIsError(false);
        try {
            const response = await apiClient.get(
                `${process.env.REACT_APP_NEW_DATA}/excludedProjects/${user_id}`
            );
            setProjectOptions(response.data || []);
        } catch (error) {
            console.error("Error fetching projects:", error);
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    };

    const resetState = () => {
        setSelectedProjects([]);
        setProjectOptions([]);
        setIsLoading(false);
        setIsError(false);
    };

    const handleClose = () => {
        resetState();
        onClose(); // Call parent-provided onClose callback
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await apiClient.post(
                `${process.env.REACT_APP_NEW_DATA}/assignProjects`,
                {
                    user_id: user.user_id,
                    projects: selectedProjects,
                }
            );
            // console.log("Projects assigned successfully:", response.data);
            setProjectOptions((prevOptions) =>
                prevOptions.filter((project) => !selectedProjects.includes(project))
            );
            setSelectedProjects([]);
            if (onSubmit) {
                onSubmit(); // Execute the parent-provided onSubmit callback
            }
        } catch (error) {
            console.error("Error assigning projects:", error);
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    border: '2px solid #000',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '8px',
                }}
            >
                <IconButton
                    sx={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                    }}
                    onClick={handleClose}
                    aria-label="Close"
                >
                    <CloseIcon />
                </IconButton>

                <Typography variant="h6" textAlign="center" mb={2}>
                    Assign Projects
                </Typography>

                {isLoading ? (
                    <Typography>Loading projects...</Typography>
                ) : isError ? (
                    <Typography color="error">Error loading projects</Typography>
                ) : projectOptions.length === 0 ? (
                    <Typography textAlign="center" color="text.secondary">
                        No projects to assign
                    </Typography>
                ) : (
                    <FormControl fullWidth>
                        <InputLabel>Projects</InputLabel>
                        <Select
                            multiple
                            value={selectedProjects}
                            onChange={(e) => setSelectedProjects(e.target.value)}
                            input={<OutlinedInput label="Projects" />}
                            renderValue={(selected) => selected.join(', ')}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 200,
                                        overflow: 'auto',
                                    },
                                },
                            }}
                        >
                            {projectOptions.map((project) => (
                                <MenuItem key={project} value={project}>
                                    <Checkbox checked={selectedProjects.indexOf(project) > -1} />
                                    <ListItemText primary={project} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    onClick={handleSubmit}
                    fullWidth
                    sx={{ mt: 2 }}
                    disabled={selectedProjects.length === 0}
                >
                    {selectedProjects.length === 0 ? "No Projects to Assign" : "ADD PROJECTS"}
                </Button>
            </Box>
        </Modal>
    );
};

export default ProjectPopup;
